import React, { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { useColorModeValue } from 'utils/hooks';
import { SizableText, Stack, useTheme, Input } from 'tamagui';
import * as WebBrowser from 'expo-web-browser';
import * as Google from 'expo-auth-session/providers/google';
import * as Facebook from 'expo-auth-session/providers/facebook';
import { profileSave } from 'reducers/slices/profile';
import { toggleLogin } from 'reducers/slices/loginstatus';
import { emailContraints } from 'components/utility/LoginEmailConstraints';
import { validate } from 'validate.js';
import appleAuth from 'components/utility/AppleAuth';
import Purchases from 'revenuecat/Purchases';
import { ColorButton } from 'components/utility/Buttons';
import Toast from 'react-native-root-toast';
import { googleAuth, facebookAuth } from 'config/authconstants';
import { useDispatch, useSelector } from 'react-redux';
import { auth } from 'utils/Firebase';
import {
  FacebookIcon,
  AppleIcon,
  GoogleIcon,
  EmailOutlineIcon,
  EmailIcon,
  LockIcon,
} from 'app/components/utility/SVGIcons';
import {
  browserPopupRedirectResolver,
  reauthenticateWithPopup,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  linkWithCredential,
  fetchSignInMethodsForEmail,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  EmailAuthProvider,
  signInWithCredential,
  User,
  UserCredential,
  AuthProvider,
  linkWithPopup,
} from 'firebase/auth';
import { useCreateOrUpdateUserMutation } from 'utils/RTKGraphQL';

WebBrowser.maybeCompleteAuthSession();

export const useSaveUser = () => {
  const dispatch = useDispatch();
  const [createOrUpdateUser] = useCreateOrUpdateUserMutation();

  const saveUser = async (user: User) => {
    try {
      const response = await createOrUpdateUser(user);
      await dispatch(profileSave(response.data));

      if (Platform.OS !== 'web') {
        await Purchases.logIn(user.uid);
      }

      dispatch(toggleLogin(false));
    } catch (e) {
      console.log('Error saving user:', e.message);
    }
  };

  return saveUser;
};

function showToast(message: string) {
  let position: number;
  if (Platform.OS === 'web') {
    position = window.pageYOffset + 0;
  } else if (Platform.OS !== 'web') {
    position = Toast.positions.TOP + 100;
  }
  Toast.show(message.toUpperCase(), {
    position: position,
    duration: 2000,
    backgroundColor: '#c9150f',
    textColor: '#FFF',
    shadow: true,
    opacity: 1,
  });
}

interface AuthButtonProps {
  icon: React.ReactElement;
  buttonText: string;
  onPress: () => void;
  disabled?: boolean;
}

const AuthButton = ({
  icon,
  buttonText,
  onPress,
  disabled,
}: AuthButtonProps) => {
  const theme = useTheme();

  return (
    <Stack
      onPress={disabled ? null : onPress}
      disabled={disabled}
      pressStyle={{ opacity: 0.5 }}
      hoverStyle={{ borderColor: '$gray800' }}
      focusStyle={{ borderColor: '$blue400' }}
      width={250}
      height="$5xl"
      borderRadius="$xs"
      borderWidth={1}
      borderColor="$gray400"
      opacity={disabled ? 0.5 : 1}
      backgroundColor={disabled ? theme.disabled : null}
      marginTop="$md"
      py={0}
      flexDirection="row"
      alignItems="center"
      justifyContent="flex-start"
      paddingLeft="$md"
      cursor={disabled ? 'not-allowed' : 'pointer'}
    >
      <Stack width={40} justifyContent="center">
        {React.cloneElement(icon, {
          color: disabled ? '$gray400' : icon.props.color,
        })}
      </Stack>
      <SizableText
        fontSize={'$sm'}
        $sm={{
          fontSize: '$md',
        }}
        color={disabled ? theme.disabledText : theme.mainColor.get()}
        marginLeft="$md"
      >
        {buttonText}
      </SizableText>
    </Stack>
  );
};

interface FacebookAuthBtnProps {
  onAuthenticate: (response: any, provider: string) => void;
  disabled?: boolean;
}

const FacebookAuthBtn = (props: FacebookAuthBtnProps) => {
  const [request, response, promptAsync] =
    Facebook.useAuthRequest(facebookAuth);

  useEffect(() => {
    if (response && typeof props.onAuthenticate === 'function') {
      props.onAuthenticate(response, 'Facebook');
    }
  }, [response]);

  const onButtonPress = async () => {
    try {
      await promptAsync();
    } catch (error) {
      console.error('Facebook authentication error:', error);
    }
  };

  return (
    <AuthButton
      disabled={props.disabled}
      icon={
        <FacebookIcon
          size={26}
          color="#4968ad"
          style={{ alignSelf: 'center', margin: 2 }}
        />
      }
      onPress={onButtonPress}
      buttonText={'Sign In with Facebook'}
    />
  );
};

interface AppleAuthBtnProps {
  disabled?: boolean;
}

const AppleAuthBtn = (props: AppleAuthBtnProps) => {
  const theme = useTheme();
  const saveUser = useSaveUser();

  const onAppleButtonPress = async () => {
    try {
      const appleAuthRequestResponse = await appleAuth.performRequest({
        requestedOperation: appleAuth.Operation.LOGIN,
        requestedScopes: [appleAuth.Scope.EMAIL, appleAuth.Scope.FULL_NAME],
      });

      const { identityToken, nonce } = appleAuthRequestResponse;

      if (identityToken) {
        const provider = new OAuthProvider('apple.com');
        const credential = provider.credential({
          idToken: identityToken,
          rawNonce: nonce,
        });

        await signInWithCredential(auth, credential);
        await saveUser(auth.currentUser);
      }
    } catch (error) {
      console.error('Apple Sign In Error:', error);
    }
  };

  const onButtonPress = async () => {
    try {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');

      if (auth.currentUser) {
        await linkWithPopup(auth.currentUser, provider);
      } else {
        await signInWithPopup(auth, provider, browserPopupRedirectResolver);
      }
      await saveUser(auth.currentUser);
    } catch (error) {
      console.error('Apple Sign In Error:', error);
      showToast(error.message);
    }
  };

  return (
    <AuthButton
      disabled={props.disabled}
      icon={
        <AppleIcon
          size={26}
          color={theme.mainColor.get()}
          style={{ alignSelf: 'center', margin: 7 }}
        />
      }
      onPress={Platform.OS !== 'ios' ? onButtonPress : onAppleButtonPress}
      buttonText={'Sign In with Apple'}
    />
  );
};

interface GoogleAuthBtnProps {
  onAuthenticate: (response: any, provider: string) => void;
  disabled?: boolean;
}

const GoogleAuthBtn = (props: GoogleAuthBtnProps) => {
  const [request, response, promptAsync] = Google.useAuthRequest(googleAuth);

  useEffect(() => {
    if (response && typeof props.onAuthenticate === 'function') {
      props.onAuthenticate(response, 'Google');
    }
  }, [response]);

  const onButtonPress = async () => {
    try {
      await promptAsync();
    } catch (error) {
      console.error('Google authentication error:', error);
    }
  };

  return (
    <AuthButton
      disabled={props.disabled}
      icon={
        <GoogleIcon
          size={26}
          color="#cd5642"
          style={{ alignSelf: 'center', margin: 7 }}
        />
      }
      onPress={onButtonPress}
      buttonText={'Sign In with Google'}
    />
  );
};

const LoginPage = () => {
  const [collapse, setCollapse] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isAddingAuthMethod, setIsAddingAuthMethod] = useState(false);
  const profile = useSelector((state: any) => state.profile);
  const [existingAuthMethods, setExistingAuthMethods] = useState<string[]>([]);
  const dispatch = useDispatch();
  const saveUser = useSaveUser();

  useEffect(() => {
    const unsubscribe = firebaseListen();
    return () => unsubscribe();
  }, []);

  const submitForm = async () => {
    let form = { email, password };
    let validationResult = validate(form, emailContraints);
    if (typeof validationResult === 'undefined') {
      handleEmailAuthenticate();
    } else {
      let [key] = Object.keys(validationResult);
      alert(validationResult[key]);
    }
  };

  const fetchExistingAuthMethods = async (userEmail: string) => {
    if (!userEmail) return;
    try {
      const methods = await fetchSignInMethodsForEmail(auth, userEmail);
      setExistingAuthMethods(methods);
    } catch (error) {
      console.error('Error fetching auth methods:', error);
    }
  };

  useEffect(() => {
    if (profile && profile.email) {
      const email = profile.email;
      fetchExistingAuthMethods(email);
    }
  }, [profile]);

  const handleEmailAuthenticate = async () => {
    if (!email || !password) {
      alert('Please enter both email and password');
      return;
    }

    if (password.length < 6) {
      alert('Password should be at least 6 characters long');
      return;
    }

    try {
      const methods = await fetchSignInMethodsForEmail(auth, email);

      if (methods.length === 0) {
        await createNewAccount();
      } else if (methods.includes('password')) {
        await signInExistingUser();
      } else {
        await addEmailToExistingAccount(methods);
      }
    } catch (error) {
      console.error('Authentication error:', error);
      alert(`Authentication error: ${error.message}`);
    }
  };

  const addEmailToExistingAccount = async (methods: string[]) => {
    try {
      let currentUser = auth.currentUser;

      if (!currentUser) {
        currentUser = await authenticateWithExistingMethod(methods);
      }

      if (!currentUser) {
        throw new Error('Failed to authenticate user');
      }

      const credential = EmailAuthProvider.credential(email, password);

      try {
        const result = await linkWithCredential(currentUser, credential);
        showToast('Email added to account successfully');
      } catch (linkError) {
        if (linkError.code === 'auth/provider-already-linked') {
          showToast('This email is already linked to the account');
        } else if (linkError.code === 'auth/credential-already-in-use') {
          showToast('This email is already used by another account');
        } else if (linkError.code === 'auth/requires-recent-login') {
          try {
            await reauthenticateUser(currentUser, methods);
            const result = await linkWithCredential(currentUser, credential);
            showToast('Email added to account successfully');
          } catch (reauthError) {
            console.error(
              'Error during re-authentication second:',
              reauthError
            );
            showToast(
              'Failed to re-authenticate. Please try logging in again.'
            );
          }
        } else {
          console.error('Error during linkWithCredential:', linkError);
          showToast('Failed to add email to account');
        }
      }
    } catch (error) {
      console.error('Error adding email to existing account:', error);
      showToast(`Failed to add email to existing account: ${error.message}`);
    }
  };

  const authenticateWithExistingMethod = async (methods: string[]) => {
    if (methods.length === 0) {
      throw new Error('No authentication methods available');
    }
    const method = methods[0];
    try {
      const result = await signInWithProvider(method);
      return result.user;
    } catch (error) {
      console.error('Error authenticating with existing method:', error);
      throw error;
    }
  };

  const reauthenticateUser = async (user: User, methods: string[]) => {
    const method = methods[0];
    let provider = getProviderForMethod(method);
    try {
      const result = await reauthenticateWithPopup(
        user,
        provider,
        browserPopupRedirectResolver
      );
      return result;
    } catch (error) {
      console.error('Error during re-authentication first:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error(
          'Authentication popup was closed before completing the sign-in process'
        );
      }
      throw error;
    }
  };

  const signInWithProvider = async (providerName: string) => {
    let provider = getProviderForMethod(providerName);
    try {
      return await signInWithPopup(
        auth,
        provider,
        browserPopupRedirectResolver
      );
    } catch (error) {
      console.error('Error signing in with provider:', error);
      throw error;
    }
  };

  const getProviderForMethod = (method: string): AuthProvider => {
    switch (method) {
      case 'google.com':
        return new GoogleAuthProvider();
      case 'facebook.com':
        return new FacebookAuthProvider();
      case 'apple.com':
        return new OAuthProvider('apple.com');
      default:
        throw new Error(`Unsupported authentication method: ${method}`);
    }
  };

  const createNewAccount = async () => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      showToast('New account created successfully');
      await saveUser(userCredential.user);
    } catch (error) {
      console.error('Error creating new account:', error);
      showToast('Failed to create new account: ' + error.message);
    }
  };

  const signInExistingUser = async () => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      showToast('Signed in successfully');
      await saveUser(userCredential.user);
    } catch (error) {
      console.error('Error signing in:', error);
      showToast('Failed to sign in: ' + error.message);
    }
  };

  const handleAuthenticate = async (response: any, provider: string) => {
    if (response?.type === 'success') {
      const { access_token } = response.params;
      let credential;
      let authProvider;

      if (provider === 'Facebook') {
        authProvider = new FacebookAuthProvider();
        credential = FacebookAuthProvider.credential(access_token);
      } else if (provider === 'Google') {
        authProvider = new GoogleAuthProvider();
        credential = GoogleAuthProvider.credential(null, access_token);
      }

      await handleCredential(credential, provider);
    }
  };

  const handleCredential = async (credential: any, provider: string) => {
    try {
      if (auth.currentUser) {
        await linkWithCredential(auth.currentUser, credential);
        showToast(`${provider} account linked successfully`);
      } else {
        const result = await signInWithCredential(auth, credential);
        showToast(`Signed in with ${provider} successfully`);
        await saveUser(result.user);
      }
    } catch (error) {
      console.error('Authentication error:', error);
      showToast(error.message);
    }
  };

  const firebaseListen = () => {
    return auth.onAuthStateChanged(async (user) => {
      if (user === null) return;

      if (isAddingAuthMethod) {
        await updateUserProviders(user);
      } else if (!profile) {
        if (
          user.emailVerified ||
          user.providerData[0].providerId === 'facebook.com'
        ) {
          await saveUser(user);
        } else if (!user.emailVerified) {
          showToast('Please verify your email!');
          try {
            await user.sendEmailVerification();
          } catch (error) {
            console.error('Error sending verification email:', error);
            showToast('Failed to send verification email');
          }
        }
      }
    });
  };

  const updateUserProviders = async (user: User) => {
    try {
      const providers = await fetchSignInMethodsForEmail(auth, user.email);
      setIsAddingAuthMethod(false);
      showToast('New authentication method added successfully!');
    } catch (error) {
      console.error('Error updating user providers:', error);
      showToast('Failed to add new authentication method.');
    }
  };

  return (
    <Stack
      height={250}
      $md={{
        px: 50,
        py: 20,
      }}
    >
      <Stack justifyContent="center" alignSelf="center" alignItems="center">
        <Stack
          width="80%"
          alignItems="center"
          justifyContent="center"
          flexGrow={1}
        >
          {collapse ? (
            <MainLogin
              handleAuthenticate={handleAuthenticate}
              setCollapse={setCollapse}
              collapse={collapse}
              existingAuthMethods={existingAuthMethods}
            />
          ) : (
            <EmailLogin
              collapse={collapse}
              email={email}
              password={password}
              setEmail={setEmail}
              setPassword={setPassword}
              submitForm={submitForm}
              setCollapse={setCollapse}
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

interface MainLoginProps {
  handleAuthenticate: (response: any, provider: string) => void;
  setCollapse: (collapse: boolean) => void;
  collapse: boolean;
  existingAuthMethods: string[];
}

const MainLogin = (props: MainLoginProps) => {
  const theme = useTheme();
  return (
    <Stack>
      <FacebookAuthBtn
        onAuthenticate={props.handleAuthenticate}
        disabled={props.existingAuthMethods.includes('facebook.com')}
      />
      <GoogleAuthBtn
        onAuthenticate={props.handleAuthenticate}
        disabled={props.existingAuthMethods.includes('google.com')}
      />
      {Platform.OS !== 'android' ? (
        <AppleAuthBtn
          disabled={props.existingAuthMethods.includes('apple.com')}
        />
      ) : null}
      <AuthButton
        icon={
          <EmailOutlineIcon
            size={26}
            color={theme.mainColor.get()}
            style={{ alignSelf: 'center', margin: 7 }}
          />
        }
        onPress={() => props.setCollapse(!props.collapse)}
        buttonText={'Sign In with Email'}
        disabled={props.existingAuthMethods.includes('password')}
      />
    </Stack>
  );
};

interface EmailLoginProps {
  collapse: boolean;
  email: string;
  password: string;
  setEmail: (email: string) => void;
  setPassword: (password: string) => void;
  submitForm: () => void;
  setCollapse: (collapse: boolean) => void;
}

const EmailLogin = (props: EmailLoginProps) => {
  const theme = useTheme();
  const mainColor = useColorModeValue('black', '#FFF');
  return (
    <Stack gap={20}>
      <AuthButton
        icon={
          <EmailOutlineIcon
            size={26}
            color={theme.mainColor.get()}
            style={{ alignSelf: 'center', margin: 7 }}
          />
        }
        onPress={() => props.setCollapse(!props.collapse)}
        buttonText={'Sign In with Email'}
      />

      <Stack alignItems="center" gap={10}>
        <Input
          py={15}
          borderRadius={0}
          width="100%"
          InputLeftElement={
            <EmailIcon
              size={26}
              color={theme.iconColor}
              style={{ alignSelf: 'center', margin: 7 }}
            />
          }
          nativeID="email"
          defaultValue={props.email}
          autoCapitalize="none"
          autoCorrect={false}
          placeholder="Email"
          onChangeText={(_email) => props.setEmail(_email)}
          textContentType="emailAddress"
          style={{ color: mainColor }}
        />

        <Input
          py={15}
          mb={10}
          borderRadius={0}
          width="100%"
          InputLeftElement={
            <LockIcon
              size={26}
              color={theme.mainColor.get()}
              style={{ alignSelf: 'center', margin: 7 }}
            />
          }
          nativeID="password"
          defaultValue={props.password}
          autoCapitalize="none"
          autoCorrect={false}
          placeholder="Password"
          onChangeText={(_password) => props.setPassword(_password)}
          textContentType="password"
          secureTextEntry={true}
          style={{ color: mainColor }}
        />

        <ColorButton
          title="Submit"
          onPress={() => props.submitForm()}
          containerStyle={{ width: 200 }}
        />
      </Stack>
    </Stack>
  );
};

export default LoginPage;
